import { UtilRequiredKeys, IpAddress } from './common';

export type ApiPaginatedAnalyticsSettingsResponse = {
  results?: AnalyticsSettingsGet[];
};

export interface AnalyticsBulkUpdateResponse {
  /** Provides collection of individual responses of each analytics update. */
  response?: BulkUpdateResponses;
}

/**
 * Provides collection of individual responses of each analytics update.
 * @example {"een.tamperDetectionEvent.":{"status":400,"error":{"code":400,"status":"invalidArgument","message":"Invalid Argument: Argument sensitivity is too large","details":[{"type":"errorInfo","reason":"wrongArgumentValue","domain":"apiv3","metadata":{"path":"/api/v3.0/<resource>"}},{"type":"badRequest","argumentViolations":[{"argumentName":"sensitivity","description":"Sensitivity is too large"}]}]}},"een.weaponDetectionEvent.v1":{"status":204}}
 */
export type BulkUpdateResponses = Record<string, BulkUpdateResponse>;

export interface BulkUpdateResponse {
  status: number;
  error?: object;
}

/** @example "een.tamperDetectionEvent.v1" */
export type AnalyticsType = string;

/** @example "een.bridgeTampering" */
export type AnalyticsCreator = string;

export interface AnalyticsSettings {
  type?: AnalyticsType;
  /** @example true */
  enabled?: boolean;
  supportedCreators?: CreatorDescription[];
}

export type AnalyticsSettingsPatch = AnalyticsSettings & {
  configuration?: ConfigurationPatch;
};

export type ConfigurationPatch =
  | ConfigurationWithoutSettingsPatch
  | EenBridgeTamperingConfiguration
  | CountingAndCrossingConfiguration
  | IntrusionConfiguration
  | LoiteringConfiguration
  | MotionConfiguration
  | ObjectDetectionConfiguration
  | LprConfiguration
  | PersonDetectionConfiguration
  | VehicleDetectionConfiguration
  | EEVAAnalyticsConfigurationPatch;

export interface AnalyticsSettingsPatchSingle {
  enabled?: boolean;
  configuration?: ConfigurationPatch;
}

export type AnalyticsSettingsGet = AnalyticsSettings & {
  configuration:
    | ConfigurationWithoutSettingsGet
    | EenBridgeTamperingConfigurationGet
    | ConfigurationWithDisabledAnalytics
    | CountingAndCrossingConfigurationGet
    | IntrusionConfigurationGet
    | LoiteringConfigurationGet
    | MotionConfigurationGet
    | ObjectDetectionConfigurationGet
    | LprConfigurationGet
    | PersonDetectionConfigurationGet
    | VehicleDetectionConfigurationGet
    | EEVAAnalyticsConfigurationGet;
};

export type ConfigurationWithDisabledAnalytics = object;

export interface ConfigurationWithoutSettingsPatch {
  creatorId?: AnalyticsCreator;
}

export interface ConfigurationWithoutSettingsGet {
  creatorId: AnalyticsCreator;
}

export interface EenBridgeTamperingConfiguration {
  /**
   * @min 0
   * @multipleOf 0.01
   * @max 1
   * @default 0.8
   */
  sensitivity?: number;
  creatorId?: 'een.bridgeTampering';
}

export type EenBridgeTamperingConfigurationGet = EenBridgeTamperingConfiguration & object;

/**
 * @format double
 * @min 0
 * @max 1
 */
export type PointCoordinate = number;

/**
 * @maxItems 50
 * @minItems 4
 * @example [[0.390625,0.19444444444444445],[0.690625,0.19444444444444445],[0.690625,0.49444444444444446],[0.390625,0.49444444444444446]]
 */
export type Polygon = PointCoordinate[][];

/**
 * @maxItems 2
 * @minItems 2
 * @default [[0.1,0.1],[0.9,1]]
 */
export type LineCoordinates = PointCoordinate[][];

export interface LineSettings {
  id?: string;
  /**
   * @maxLength 126
   * @default ""
   */
  name?: string;
  /** @default "up" */
  direction?: 'up' | 'down' | 'left' | 'right';
  coordinates: LineCoordinates;
}

/**
 * Only one line is allowed. The line is required. For PATCH method the action depends on the id field. If the id is not present then a new line will be created. If the id is present then the line will be updated.
 * @maxItems 1
 * @minItems 1
 */
export type Lines = LineSettings[];

export type CountingAndCrossingConfigurationGet = UtilRequiredKeys<
  CountingAndCrossingConfiguration,
  'lines' | 'creatorId'
> & {
  lines: object[];
};

export interface CountingAndCrossingConfiguration {
  /** Only one line is allowed. The line is required. For PATCH method the action depends on the id field. If the id is not present then a new line will be created. If the id is present then the line will be updated. */
  lines?: Lines;
  creatorId?: 'een.bridgeCounting' | 'een.bridgeLineCrossing';
}

export type IntrusionConfigurationGet = IntrusionConfiguration & {
  regions: object[];
};

/** At least one region needs to be given. */
export interface IntrusionConfiguration {
  /** For PATCH method the action depends on the id field. If the id is not present then a new region will be created. If the id is present then the region with this id will be updated. If the region is missing on the list then it will be deleted. */
  regions?: Regions;
  creatorId?: 'een.bridgeIntrusionDetection';
}

/**
 * For PATCH method the action depends on the id field. If the id is not present then a new region will be created. If the id is present then the region with this id will be updated. If the region is missing on the list then it will be deleted.
 * @maxItems 50
 * @minItems 1
 */
export type Regions = Region[];

export interface Region {
  id?: string;
  /**
   * @maxLength 126
   * @default ""
   * @example "New Region"
   */
  name?: string;
  polygon: Polygon;
}

export type LoiteringRegion = Region & {
  /**
   * Duration in seconds that a person or object remains within a defined area.
   * @min 1
   * @default 10
   * @example 10
   */
  dwellTime?: number;
};

/**
 * For PATCH method the action depends on the id field. If the id is not present then a new region will be created. If the id is present then the region with this id will be updated. If the region is missing on the list then it will be deleted.
 * @maxItems 50
 * @minItems 1
 */
export type LoiteringRegions = LoiteringRegion[];

/** At least one region needs to be given. */
export interface LoiteringConfiguration {
  /** For PATCH method the action depends on the id field. If the id is not present then a new region will be created. If the id is present then the region with this id will be updated. If the region is missing on the list then it will be deleted. */
  regions?: LoiteringRegions;
  creatorId?: 'een.bridgeLoitering';
}

export type LoiteringConfigurationGet = LoiteringConfiguration & {
  regions: object[];
};

/**
 * @format double
 * @min 0
 * @max 1
 */
export type PercentValue = number;

export type Sensitivity = PercentValue;

/**
 * The motion detection system looks for objects moving through the image. The size selection helps to filter out unimportant motions. Explanation of options:
 *    * `small` - Objects that take up about 1% of the camera's total image.
 *    * `medium` - Objects that take up around 5% of the image.
 *    * `large` - Objects that are approximately 10% of the full image size.
 * @default "small"
 */
export enum MotionObjectSize {
  Small = 'small',
  Medium = 'medium',
  Large = 'large',
}

export type MotionRegion = Region & {
  sensitivity?: Sensitivity;
  /**
   * The motion detection system looks for objects moving through the image. The size selection helps to filter out unimportant motions. Explanation of options:
   *    * `small` - Objects that take up about 1% of the camera's total image.
   *    * `medium` - Objects that take up around 5% of the image.
   *    * `large` - Objects that are approximately 10% of the full image size.
   */
  objectSize?: MotionObjectSize;
};

/**
 * For PATCH method the action depends on the id field. If the id is not present then a new region will be created. If the id is present then the region with this id will be updated. If the region is missing on the list then it will be deleted.
 * @maxItems 50
 */
export type MotionRegions = MotionRegion[];

export interface MotionConfiguration {
  /** The default level of motion sensitivity that is applied to the entire image. Regions have their own sensitivity setting that will override the this value.  */
  sensitivity?: Sensitivity;
  /**
   * The default object size that is applied to the entire image. The size selection helps to filter out unimportant motions. Created regions have their own object size value that overrides this value. Explanation of options:
   *   * `small` - Objects that take up about 1% of the camera's total image.
   *   * `medium` - Objects that take up around 5% of the image.
   *   * `large` - Objects that are approximately 10% of the full image size.
   */
  objectSize?: MotionObjectSize;
  /** For PATCH method the action depends on the id field. If the id is not present then a new region will be created. If the id is present then the region with this id will be updated. If the region is missing on the list then it will be deleted. */
  regions?: MotionRegions;
  creatorId?: 'een.bridgeMotion';
}

export type MotionConfigurationGet = MotionConfiguration & {
  regions: object[];
};

/** The minimum size object to detect. */
export interface MinimumObjectSize {
  width?: PercentValue;
  height?: PercentValue;
}

/** The maximum size object to detect. */
export interface MaximumObjectSize {
  width?: PercentValue;
  height?: PercentValue;
}

export interface ObjectDetectionConfiguration {
  sensitivity?: Sensitivity;
  /** The minimum size object to detect. */
  minimumObjectSize?: MinimumObjectSize;
  /** The maximum size object to detect. */
  maximumObjectSize?: MaximumObjectSize;
  creatorId?: 'een.bridgeCommonConfiguration';
}

export type ObjectDetectionConfigurationGet = ObjectDetectionConfiguration & {
  minimumObjectSize: object;
  maximumObjectSize: object;
};

/**
 * @maxItems 4
 * @minItems 4
 * @example [[0.390625,0.19444444444444445],[0.690625,0.19444444444444445],[0.690625,0.49444444444444446],[0.390625,0.49444444444444446]]
 */
export type LprRegion = PointCoordinate[][];

export type LprConfigurationGet = LprConfiguration & {
  webhooks: {
    primaryWebhook: {
      settings?: object;
    };
    secondaryWebhook: {
      settings?: object;
    };
  };
};

export interface LprConfiguration {
  creatorId?: 'een.bridgeLpr';
  /**
   * This defines the input resolution of the camera video for LPR. The value of it should be the same as the value in the camera settings. If the value will be different it can reduce vehicle detection efficiency. Higher resolution increases the load on the bridge. The following guidelines can help you select the optimal value:
   *   * `1280x720` - For lane width (camera view) less than 3.5 meters
   *   * `1920x1080` - For lane width (camera view) between 3.5 and 7 meters
   * @default "1280x720"
   */
  processingResolution?: '1280x720' | '1920x1080';
  /**
   * The frame rate at which the LPR is processed. The value of it should be the same as the value in the camera settings. If the value will be different it can reduce vehicle detection efficiency. Frame rate should be chosen based on expected vehicle speed. Higher frame rates increase the load on the bridge. The following guidelines can help you select the optimal value:
   *   * Gate LPR (speed less than 10 MPH) – 10 FPS
   *   * Street LPR (speed less than 30 MPH) – 15 FPS
   *   * Highway LPR (speed less than 70 MPH) – 20 FPS
   *   * Highway LPR (Speed less than 100 MPH) - 30 FPS
   * @default 10
   */
  processingRate?: 10 | 15 | 20 | 30;
  /** @default "US" */
  country?: 'US' | 'Europe' | 'Malaysia' | 'Singapore' | 'India' | 'Australia' | 'Ghana' | 'Taiwan';
  /** The Region of Interest (ROI) is a designated area where the vehicle and license plate are detected and tracked, ensuring that the system focuses solely on relevant area while disregarding area outside the ROI. More information can be found in the developer guide. */
  detectionRegion?: LprRegion & object;
  /** A designated region of interest (ROI) used to capture license plates for license plate reads, prioritizing these plates when generating events. This ROI is strategically positioned to ensure the plates are larger, closer to the camera, and clearer for optimal recognition. More information can be found in the developer guide. */
  triggerRegion?: LprRegion & object;
  /**
   * Use cases:
   *   * Used to determine vehicle entry and exit within a lot.
   *   * Supports reconciliation and authentication features.
   *   * Calculates transaction types and occupancy by tracking entry and exit counts.
   *   * Filters out vehicles moving in the opposite direction (for entry or exit) based on access type.
   *   * Applicable in cases where a camera covers multiple lanes, or vehicles may travel in both directions within a single lane, allowing focus on vehicles either entering, exiting, or both.
   *
   * This setting controls vehicle direction and applies filters to ignore vehicles moving in the opposite direction.
   *   * `entry` - Vehicle is entering the premises.
   *   * `exit` - Vehicle is exiting the premises.
   *   * `biDirectional` - Vehicles are expected to move in both directions.
   * @default "biDirectional"
   */
  direction?: 'entry' | 'exit' | 'biDirectional';
  /** This is to specify which direction of the vehicle travelling is considered entry to facility or exit to facility. As the view (of the vehicle) is two dimensional, we define the  vehicle's direction using the vehicle's relative motion in either horizontal axis or vertical axis. Vehicles should cross the line in perpendicular, but all the vehicles may not cross perpendicular to the line, so we count the vehicles that pass the similar angle. */
  directionLine?: object & LineCoordinates;
  /** A direction for directionLine. */
  entryDirection?: 'up' | 'down' | 'left' | 'right';
  /** Allows to integrate with external access control system using locally stored license plate information. */
  localId?: LocalId;
  /** Allows LPR Events to be sent to a third-party endpoint. */
  webhooks?: Webhooks;
}

/** Allows to integrate with external access control system using locally stored license plate information. */
export interface LocalId {
  enabled: boolean;
  settings?: LocalIdSettings;
}

export type LocalIdSettings =
  | ({
      type: 'externalIO';
    } & LocalIdSettingsExternalIO)
  | ({
      type: 'brivo';
    } & LocalIdSettingsBrivo);

/**
 * A PIN that is used to grant access for authorized vehicles.
 * @min 0
 * @max 999
 */
export type AllowedPin = number;

/**
 * A PIN that is used to restrict access for unauthorized vehicles.
 * @min 0
 * @max 999
 */
export type RestrictedPin = number;

/**
 * A PIN that is used to perform some action or trigger for unregistered vehicles.
 * @min 0
 * @max 999
 */
export type UnlistedPin = number;

/** Serial number of the USB to RS485 Converter used for access control. */
export type UsbSerialNumber = string;

export type LocalIdSettingsExternalIO =
  | ({
      externalIoModule: 'usbRelay';
    } & ExternalIoUsbRelay)
  | ({
      externalIoModule: 'moxaIoLogik';
    } & ExternalIoMoxaIoLogik);

export interface ExternalIoBase {
  /** Defines the method of integrating LPR with external access control systems. The `externalIO` utilizes external devices such as USB Relay, Moxa Relay, etc., for access control.     */
  type: 'externalIO';
}

export type ExternalIoUsbRelay = ExternalIoBase & {
  /** A hardware interface that facilitates communication between the LPR system and physical devices via input/output signals. */
  externalIoModule?: 'usbRelay';
  /** A PIN that is used to grant access for authorized vehicles. */
  allowedPin: AllowedPin;
  /** A PIN that is used to restrict access for unauthorized vehicles. */
  restrictedPin: RestrictedPin;
  /** A PIN that is used to perform some action or trigger for unregistered vehicles. */
  unlistedPin: UnlistedPin;
  /** Serial number of the USB to RS485 Converter used for access control. */
  usbSerialNumber: UsbSerialNumber;
};

export type ExternalIoMoxaIoLogik = ExternalIoBase & {
  /** A hardware interface that facilitates communication between the LPR system and physical devices via input/output signals. */
  externalIoModule?: 'moxaIoLogik';
  /** The IP address of external device like Moxa Relay, allowing the LPR system to communicate with it over the network to trigger the signals. */
  ipAddress: IpAddress;
  /** A PIN that is used to grant access for authorized vehicles. */
  allowedPin: AllowedPin;
  /** A PIN that is used to restrict access for unauthorized vehicles. */
  restrictedPin: RestrictedPin;
  /** A PIN that is used to perform some action or trigger for unregistered vehicles. */
  unlistedPin: UnlistedPin;
};

export interface LocalIdSettingsBrivo {
  /** Defines the method of integrating LPR with external access control systems. With the `brivo` optino LPR communicates with the locally connected Brivo panel, using the license plate as credentials to trigger gate access via Brivo. */
  type: 'brivo';
  /** Serial number of the USB to RS485 Converter used for access control. */
  usbSerialNumber: UsbSerialNumber;
  /**
   * Select the device ID based on below conditions:
   *   * `pd0` - If no other reader is connected to the door.
   *   * `pd1` - If any other reader is connected to the door.
   */
  peripheralDeviceId: 'pd0' | 'pd1';
}

/** Allows LPR Events to be sent to a third-party endpoint. */
export interface Webhooks {
  primaryWebhook?: Webhook;
  secondaryWebhook?: Webhook;
}

export interface Webhook {
  enabled: boolean;
  settings?: WebhookSettings;
}

export interface WebhookSettings {
  /**
   * Optional code that allows customers to override the default webhook payload format.  When provided, this value adjusts the payload format to match the specific requirements  defined for the customer.
   * @minLength 0
   * @maxLength 50
   */
  clientConnector?: string;
  /** Different customers may need custom integration requirement which is served by a connector or client adapter. If the `custom` option is chosen, then the payload format will follow requirements from the `clientConnector` field. */
  clientConnectorType: 'default' | 'custom';
  /**
   * Enter the URL for receiving the events created by the client.
   * @minLength 0
   * @maxLength 999
   * @example "https://example.com/path?param=test"
   */
  url: string;
  /**
   * Headers in an API POST request are key-value pairs sent to the server that provide essential information about the request.
   * @default {}
   * @example {"token":"xxxxxxxx"}
   */
  headers?: object;
  /**
   * Optional custom keys that the client may need to include when sending the event JSON. For example, a device or lane identifier from the client can be mapped in this object.
   * @default {}
   * @example {"laneId":"20"}
   */
  customFields?: object;
  /**
   * Indicates whether to retry sending unsynced events. Options description:
   *   * never - Choosing this option will result in unsynced events being completely skipped by the system after the first attempt, regardless of the status response.
   *   * 30sec, 1min, 1hour - With this setting, if the endpoint is unreachable or not accepting events after the specified time, the system will skip events older than the selected interval.
   *   * always - This option ensures that all unsynced events are saved locally, and the system will continue to attempt to send them to the client endpoint until all events are successfully transmitted.
   * @default "never"
   */
  retry?: '30sec' | '1min' | '1hour' | 'never' | 'always';
  /**
   * This setting specifies how you would like to receive the LPR event images (Full Vehicle Image and License Plate Crop). Choose the image type based on your integration requirements:
   *   * `noImages` - No images will be included with the LPR JSON payload.
   *   * `base64` - LPR event images will be sent as Base64-encoded data.
   */
  imageFormat: 'noImages' | 'base64';
}

/** @maxItems 50 */
export type CloudAnalyticsRegions = {
  /** ID of the selected motion region. */
  motionRegionId: string;
  /** The minimum size of an object to be detected, expressed as a fraction of the image size. */
  minimumObjectSize?: PercentValue;
  /** The maximum size of an object to be detected, expressed as a fraction of the image size. */
  maximumObjectSize?: PercentValue;
  /** The minimum object confidence to be detected. */
  minimumClassificationConfidence?: PercentValue;
  /**
   * Indicates if stationary objects should be included.
   * @default false
   */
  includeStationaryObjects?: boolean;
}[];

export type CloudAnalyticsPolygon = Polygon;

export interface CloudConfigurationWithRegionProperties {
  regions?: {
    /** If specified, an existing motion region with the given ID will be used. Any changes specific to this region should be made directly in the een.motionInRegionDetectionEvent.v1 configuration. This field must not be used in combination with name and polygon. */
    motionRegionId?: any;
    /** A new motion region will be created and assigned to the analytics if the polygon is present. The polygon cannot be present along with the motionRegionId. */
    polygon?: CloudAnalyticsPolygon;
    /**
     * The name of a new motion region, used when creating a region via polygon. Must not be used together with motionRegionId, which refers to an existing region.
     * @maxLength 126
     * @example "New Region"
     */
    name?: string;
  }[];
}

export interface CloudConfigurationWithRequiredFields {
  regions: object[];
}

export interface PersonDetectionConfiguration {
  regions?: CloudAnalyticsRegions;
  creatorId?: 'een.cloudPersonDetection';
}

export type PersonDetectionConfigurationGet = PersonDetectionConfiguration & {
  regions: object[];
};

export interface VehicleDetectionConfiguration {
  regions?: CloudAnalyticsRegions;
  creatorId?: 'een.cloudVehicleDetection';
}

export type VehicleDetectionConfigurationGet = VehicleDetectionConfiguration & {
  regions: object[];
};

export type EEVAAnalyticsConfiguration = (EEVABinaryQueryEvent | EEVABinaryQuerySchedule) & {
  creatorId?: 'een.eevaBinaryQuery';
};

export type EEVAAnalyticsConfigurationGet = EEVAAnalyticsConfiguration & CloudConfigurationWithRequiredFields;

export type EEVAAnalyticsConfigurationPatch = EEVAAnalyticsConfiguration & CloudConfigurationWithRegionProperties;

export interface EEVABinaryQueryCommon {
  /** The trigger mode determines how the analytics are activated. The `event` option triggers the analytics based on specific events, while the `schedule` option activates them according to a predefined schedule. */
  triggerMode: string;
  /**
   * A single-word label that would be related to the query.  This label would be used as an alias in Video search and history browser instead of the long verbose query.
   * @maxLength 25
   */
  label: string;
  /**
   * A true or false question defined by the user which will generate an event when the answer matches the eventCreateOn value.
   * @maxLength 1024
   */
  query: string;
  /** This key indicates when the event should be generated.  As the VLM query is expected to respond as yes/ no, based on the value of this variable, the event would be generated for only one of the responses. */
  eventTriggerValue: boolean;
  schedule: any;
}

export type EEVABinaryQueryEvent = EEVABinaryQueryCommon & {
  triggerMode?: 'event';
  /** This setting is available only when triggerMode is set to event. Array of strings. Empty value would indicate processing the analytics on any available eventType that is received. The event types on which the analytics has to perform. */
  eventType__in: ('een.personDetectionEvent.v1' | 'een.vehicleDetectionEvent.v1' | 'een.sceneLabelEvent.v1')[];
  /**
   * This variable represents the minimum time difference in seconds between the execution of this analytics.
   * @min 60
   * @max 86400
   * @example 120
   */
  coolDownPeriodSeconds: number;
};

export type EEVABinaryQuerySchedule = EEVABinaryQueryCommon & {
  triggerMode?: 'schedule';
  /**
   * This setting is available only when triggerMode is set to schedule.  This variable represents the interval in seconds at which the event should be processed.
   * @min 60
   * @max 86400
   * @example 120
   */
  intervalSeconds: number;
};

export interface CreatorDescription {
  /** @example "een.bridgeTampering" */
  id: string;
  /** Indicates if enabling analytics from this creator affects billing. */
  affectsBilling?: boolean;
  /** @example "bridge" */
  performedOn: 'bridge' | 'camera' | 'cloud';
  configuration: CreatorConfigurationDescription;
}

export type CreatorConfigurationDescription =
  | ({
      type: 'settings';
    } & SettingsCreatorConfiguration)
  | ({
      type: 'tunnel';
    } & TunnelCreatorConfiguration)
  | ({
      type: 'note';
    } & NoteCreatorConfiguration);

export interface TunnelCreatorConfiguration {
  type: 'tunnel';
}

export interface NoteCreatorConfiguration {
  type: 'note';
}

export interface SettingsCreatorConfiguration {
  type: 'settings';
  /** @example {"required":["sensitivity"],"properties":{"sensitivity":{"type":"number","minimum":0,"maximum":1,"default":0.1,"multipleOf":0.01}}} */
  schema: object;
}
